import React, { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useSelector } from 'react-redux';
import { selectCurrentAdmin } from '@/redux/auth/selectors';
import { BASE_URL } from '@/config/serverApiConfig';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {
  Button,
  Upload,
  Table,
  Modal,
  message,
  Space,
  Typography,
  Card,
  Row,
  Col,
  Spin,
  Popconfirm,
  Tag,
  Layout,
  Divider,
  Statistic,
  Progress,
  Tooltip
} from 'antd';
import {
  UploadOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  LeftOutlined,
  RightOutlined,
  FilePdfOutlined,
  HistoryOutlined,
  MailOutlined,
  DownloadOutlined,
  DeleteOutlined,
  FullscreenOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  TeamOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { useTheme } from '@/context/theme';

const { Title, Text } = Typography;
const { Dragger } = Upload;
const { Header, Content, Sider } = Layout;

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const EmployeeHandbook = () => {
  const { isDarkMode } = useTheme();
  const currentAdmin = useSelector(selectCurrentAdmin);
  const [scale, setScale] = useState(1.0);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [handbookFile, setHandbookFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [uploadHistory, setUploadHistory] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [fileName, setFileName] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [trackingModalVisible, setTrackingModalVisible] = useState(false);
  const [trackingData, setTrackingData] = useState({ stats: {}, list: [] });
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [selectedHandbookId, setSelectedHandbookId] = useState(null);

  // Check if user has HR/Admin access
  const hasHRAdminAccess = () => {
    const userRole = currentAdmin?.role;
    return ['super_admin', 'hr_admin', 'Admin'].includes(userRole);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') nextPage();
      if (e.key === 'ArrowLeft') previousPage();
    };

    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    window.addEventListener('keydown', handleKeyDown);
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [pageNumber, numPages]);

  // Load current handbook
  useEffect(() => {
    loadCurrentHandbook();
    loadUploadHistory();
  }, []);

  const loadCurrentHandbook = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/handbook/current');
      if (response.data.success) {
        let fileUrl = response.data.handbook;
        const name = response.data.fileName || 'Digital Invoicing FAQ.pdf';
        setFileName(name);
        if (!fileUrl.startsWith('http')) {
          const cleanBaseUrl = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL;
          const cleanFilePath = fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`;
          fileUrl = `${cleanBaseUrl}${cleanFilePath}`;
        }
        setHandbookFile(fileUrl);
      }
    } catch (error) {
      console.error('Error loading handbook:', error);
      const cleanBaseUrl = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL;
      const fallbackUrl = `${cleanBaseUrl}/Dymaxtech-Handbook.pdf`;
      setHandbookFile(fallbackUrl);
    } finally {
      setLoading(false);
    }
  };

  const loadUploadHistory = async () => {
    try {
      const response = await axios.get('/handbook/history');
      if (response.data.success) {
        setUploadHistory(response.data.history);
      }
    } catch (error) {
      console.error('Error loading upload history:', error);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const changePage = (offset) => {
    setPageNumber(prevPageNumber => {
      const newPageNumber = prevPageNumber + offset;
      if (newPageNumber < 1) return 1;
      if (newPageNumber > numPages) return numPages;
      return newPageNumber;
    });
  };

  const previousPage = () => {
    if (pageNumber > 1) changePage(-1);
  };

  const nextPage = () => {
    if (pageNumber < numPages) changePage(1);
  };

  const zoomIn = () => {
    setScale(prevScale => Math.min(prevScale + 0.2, 3.0));
  };

  const zoomOut = () => {
    setScale(prevScale => Math.max(prevScale - 0.2, 0.5));
  };

  const handleUpload = async (file) => {
    if (file.type !== 'application/pdf') {
      message.error('Only PDF files are allowed!');
      return false;
    }

    const formData = new FormData();
    formData.append('handbook', file);
    formData.append('uploadedBy', currentAdmin?._id);
    formData.append('uploadedByName', `${currentAdmin?.firstName || ''} ${currentAdmin?.lastName || ''}`.trim() || currentAdmin?.name || 'Admin');

    try {
      setUploading(true);
      const response = await axios.post('/handbook/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        message.success('Handbook uploaded successfully!');
        setUploadModalVisible(false);
        loadCurrentHandbook();
        loadUploadHistory();
      } else {
        message.error(response.data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      message.error('Failed to upload handbook');
    } finally {
      setUploading(false);
    }

    return false;
  };

  const sendNotificationEmail = async () => {
    try {
      setLoading(true);
      const response = await axios.post('/handbook/notify');
      if (response.data.success) {
        message.success('Notification email sent to all employees!');
        loadUploadHistory();
      } else {
        message.error(response.data.message || 'Failed to send notification');
      }
    } catch (error) {
      console.error('Notification error:', error);
      message.error('Failed to send notification email');
    } finally {
      setLoading(false);
    }
  };

  const loadTrackingData = async (handbookId) => {
    try {
      setTrackingLoading(true);
      setSelectedHandbookId(handbookId);
      const response = await axios.get(`/handbook/acknowledgments/${handbookId}`);
      if (response.data.success) {
        setTrackingData({
          stats: response.data.stats,
          list: response.data.list
        });
        setTrackingModalVisible(true);
      }
    } catch (error) {
      console.error('Error loading tracking data:', error);
      message.error('Failed to load acknowledgment status');
    } finally {
      setTrackingLoading(false);
    }
  };

  const historyColumns = [
    {
      title: 'FILE NAME',
      dataIndex: 'fileName',
      key: 'fileName',
      width: 250,
      render: (text) => (
        <Tooltip title={text} placement="topLeft">
          <Space>
            <FilePdfOutlined style={{ color: '#ff4d4f' }} />
            <Text 
              style={{ 
                maxWidth: 180, 
                whiteSpace: 'nowrap', 
                overflow: 'hidden', 
                textOverflow: 'ellipsis',
                display: 'inline-block'
              }}
            >
              {text}
            </Text>
          </Space>
        </Tooltip>
      ),
    },
    {
      title: 'UPLOADED BY',
      dataIndex: 'uploadedByName',
      key: 'uploadedByName',
    },
    {
      title: 'UPLOAD DATE',
      dataIndex: 'uploadDate',
      key: 'uploadDate',
      render: (date) => dayjs(date).format('MMM DD, YYYY HH:mm'),
    },
    {
      title: 'VERSION',
      dataIndex: 'version',
      key: 'version',
      width: 100,
      render: (version) => <Tag color="blue" className="px-3 rounded-full font-bold">V{version}</Tag>,
    },
    {
      title: 'NOTIFICATION STATUS',
      dataIndex: 'notificationSent',
      key: 'notificationSent',
      width: 180,
      render: (sent) => (
        <Tag color={sent ? 'green' : 'orange'} className="rounded-md border-0 bg-opacity-10 px-3">
          {sent ? 'Sent' : 'Not Sent'}
        </Tag>
      ),
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      width: 300,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            type="primary"
            icon={<ZoomInOutlined />}
            onClick={() => {
              let url = record.filePath;
              if (!url.startsWith('http')) {
                const cleanBaseUrl = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL;
                const cleanFilePath = url.startsWith('/') ? url : `/${url}`;
                url = `${cleanBaseUrl}${cleanFilePath}`;
              }
              setHandbookFile(url);
              setPageNumber(1);
              setHistoryModalVisible(false);
            }}
          >
            View
          </Button>
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => {
              let url = record.filePath;
              if (!url.startsWith('http')) {
                const cleanBaseUrl = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL;
                const cleanFilePath = url.startsWith('/') ? url : `/${url}`;
                url = `${cleanBaseUrl}${cleanFilePath}`;
              }
              window.open(url, '_blank');
            }}
          >
            Download
          </Button>
          {hasHRAdminAccess() && (
            <Button
              size="small"
              icon={<TeamOutlined />}
              onClick={() => loadTrackingData(record._id)}
              loading={trackingLoading && selectedHandbookId === record._id}
            >
              Tracking
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const trackingColumns = [
    {
      title: 'Employee',
      key: 'employee',
      render: (_, record) => (
        <div className="flex flex-col">
          <Text className={`font-semibold ${isDarkMode ? 'text-gray-200' : 'text-slate-800'}`}>
            {record.employee?.firstName} {record.employee?.lastName}
          </Text>
          <Text type="secondary" className="text-[10px]">{record.employee?.email}</Text>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let color = 'gold';
        if (status === 'acknowledged') color = 'success';
        if (status === 'seen') color = 'blue';
        return (
          <Tag color={color} className="rounded-full px-3 uppercase text-[10px] font-bold">
            {status}
          </Tag>
        );
      },
    },
    {
      title: 'Acknowledgment Date',
      dataIndex: 'acknowledgmentDate',
      key: 'acknowledgmentDate',
      render: (date) => date ? (
        <div className="flex flex-col">
          <Text className={isDarkMode ? 'text-gray-300' : 'text-slate-700'}>{dayjs(date).format('MMM DD, YYYY')}</Text>
          <Text type="secondary" className="text-[10px]">{dayjs(date).format('HH:mm')}</Text>
        </div>
      ) : <Text type="secondary" className="text-xs">-</Text>,
    },
    {
      title: 'Link Status',
      key: 'linkStatus',
      render: (_, record) => {
        const isExpired = record.expiresAt && new Date() > new Date(record.expiresAt);
        if (record.status === 'acknowledged') return <Tag color="success" className="rounded-md">Valid</Tag>;
        return isExpired ?
          <Tag color="error" className="rounded-md">Expired</Tag> :
          <Tag color="processing" className="rounded-md">Active</Tag>;
      }
    }
  ];

  return (
    <Layout className={`w-[90%] max-w-none mx-auto p-4 space-y-4 min-h-screen ${isDarkMode ? 'bg-[#141414]' : 'bg-slate-50'} main-handbook-layout transition-colors duration-200`}>
      {!isFullscreen && (
        <Header className={`${isDarkMode ? 'bg-[#1f1f1f] border-gray-800' : 'bg-white border-gray-100'} px-5 h-16 flex items-center justify-between shadow-sm z-10 border rounded-t-xl transition-colors duration-200`}>
          <div className="flex items-center gap-5">
            <div className="bg-[#1677ff] rounded-md px-4 py-1.5 flex items-center justify-center shadow-sm">
              <span className="text-white font-bold text-sm tracking-wide whitespace-nowrap uppercase">Employee Handbook</span>
            </div>
            <span className={`${isDarkMode ? 'text-gray-300 bg-gray-800/50 border-gray-700' : 'text-gray-400 bg-gray-50 border-gray-100'} text-[11px] hidden md:inline-block font-semibold px-3 py-1 rounded border transition-colors duration-200`}>{fileName}</span>
          </div>

          <div className="flex items-center">
            {hasHRAdminAccess() && (
              <Space size="middle">
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={() => setUploadModalVisible(true)}
                  className="rounded-md shadow-md h-10 px-5 text-sm font-medium flex items-center"
                >
                  Upload New
                </Button>
                <Button
                  icon={<HistoryOutlined />}
                  onClick={() => setHistoryModalVisible(true)}
                  className={`rounded-md h-10 px-5 text-sm font-medium flex items-center ${isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-200 hover:text-blue-400' : 'bg-white border-gray-300 text-gray-700 hover:text-blue-600'} shadow-sm transition-all hover:border-blue-600`}
                >
                  History
                </Button>
                <Popconfirm
                  title="Send Notification"
                  description="Send this PDF to all employees?"
                  onConfirm={sendNotificationEmail}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    type="default"
                    icon={<MailOutlined />}
                    loading={loading}
                    className={`rounded-md h-10 px-5 text-sm font-medium flex items-center ${isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-200 hover:text-blue-400' : 'bg-white border-gray-300 text-gray-700 hover:text-blue-600'} shadow-sm transition-all hover:border-blue-600`}
                  >
                    Notify All
                  </Button>
                </Popconfirm>
              </Space>
            )}

            {!hasHRAdminAccess() && (
              <div className="flex items-center">
                <Tag color="blue" className="rounded-full border-none px-4 py-1 font-medium bg-blue-50 text-blue-600">Company Protocol</Tag>
              </div>
            )}
          </div>
        </Header>
      )}

      <Layout className={`${isDarkMode ? 'bg-[#141414]' : 'bg-white'} ${isFullscreen ? 'rounded-xl overflow-hidden shadow-2xl' : ''}`}>
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          width={220}
          theme={isDarkMode ? 'dark' : 'light'}
          className={`${isDarkMode ? 'border-gray-800' : 'border-gray-200'} border-r overflow-hidden transition-colors duration-200`}
          style={{ height: isFullscreen ? '100vh' : 'calc(100vh - 56px)', backgroundColor: isDarkMode ? '#1f1f1f' : '#f8fafc' }}
        >
          <div className={`flex flex-col h-full ${isDarkMode ? 'bg-[#141414]' : 'bg-[#f8fafc]'}`}>
            <div className={`p-3 border-b ${isDarkMode ? 'border-gray-800 bg-[#1f1f1f]' : 'border-gray-200 bg-white'} flex items-center justify-between transition-colors duration-200`}>
              {!collapsed && <span className={`text-[10px] font-bold ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} tracking-wider uppercase`}>Page Navigation</span>}
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                className={`${isDarkMode ? 'text-gray-500 hover:text-blue-400' : 'text-gray-400 hover:text-blue-600'} flex items-center justify-center`}
                size="small"
              />
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-6 custom-scrollbar">
              {!collapsed && numPages && Array.from(new Array(numPages), (el, index) => (
                <div
                  key={`thumb_${index + 1}`}
                  onClick={() => setPageNumber(index + 1)}
                  className={`cursor-pointer transition-all duration-200 group ${pageNumber === index + 1 ? 'ring-2 ring-blue-500 ring-offset-2' : ''}`}
                >
                  <div className={`shadow-sm border rounded p-1 flex justify-center overflow-hidden transition-colors duration-200 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 group-hover:border-blue-300'}`}>
                    <Document file={handbookFile} loading={<div className={`h-40 w-full animate-pulse ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`} />}>
                      <Page
                        pageNumber={index + 1}
                        width={160}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    </Document>
                  </div>
                  <div className={`text-center mt-2 text-[11px] font-medium transition-colors duration-200 ${pageNumber === index + 1 ? 'text-blue-500' : (isDarkMode ? 'text-gray-400' : 'text-gray-500')}`}>
                    Page {index + 1}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Sider>

        <Content className={`${isDarkMode ? 'bg-black' : 'bg-slate-100'} flex flex-col transition-colors duration-200`} style={{ height: isFullscreen ? '100vh' : 'calc(100vh - 56px)' }}>
          <div className={`${isDarkMode ? 'bg-[#1f1f1f] border-gray-800' : 'bg-white border-gray-200'} h-10 px-6 flex items-center justify-between border-b shadow-sm z-10 transition-colors duration-200`}>
            <div className="flex items-center gap-6">
              <Space className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-100'} px-2 py-0.5 rounded border transition-colors duration-200`}>
                <Button
                  type="text"
                  size="small"
                  icon={<LeftOutlined className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />}
                  onClick={previousPage}
                  disabled={pageNumber <= 1}
                />
                <span className={`text-[11px] font-semibold min-w-[60px] text-center uppercase tracking-tight transition-colors duration-200 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Page <span className="text-blue-500">{pageNumber}</span> / {numPages}
                </span>
                <Button
                  type="text"
                  size="small"
                  icon={<RightOutlined className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />}
                  onClick={nextPage}
                  disabled={pageNumber >= numPages}
                />
              </Space>
            </div>
            <div className="flex items-center gap-4">
              <Space className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-100'} px-2 py-0.5 rounded border transition-colors duration-200`}>
                <Tooltip title="Zoom Out">
                  <Button type="text" size="small" icon={<ZoomOutOutlined className="text-xs" />} onClick={zoomOut} />
                </Tooltip>
                <span className={`text-[11px] font-bold w-10 text-center transition-colors duration-200 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{Math.round(scale * 100)}%</span>
                <Tooltip title="Zoom In">
                  <Button type="text" size="small" icon={<ZoomInOutlined className="text-xs" />} onClick={zoomIn} />
                </Tooltip>
              </Space>
              <Divider type="vertical" className="h-4 border-gray-300 mx-0" />
              <Tooltip title="Toggle Fullscreen">
                <Button
                  type="text"
                  size="small"
                  icon={<FullscreenOutlined className="text-sm" />}
                  onClick={() => {
                    const elem = document.querySelector('.main-handbook-layout');
                    if (!document.fullscreenElement) elem?.requestFullscreen?.();
                    else document.exitFullscreen?.();
                  }}
                />
              </Tooltip>
            </div>
          </div>

          <div className={`flex-1 overflow-auto p-8 flex justify-center pdf-container relative custom-scrollbar transition-colors duration-200 ${isDarkMode ? 'bg-[#141414]' : 'bg-slate-100'}`}>
            {loading ? <Spin size="large" className="mt-20" /> : handbookFile ? (
              <div className={`shadow-2xl border transition-colors duration-200 ${isDarkMode ? 'border-gray-800' : 'border-gray-200'} bg-white`}>
                <Document
                  file={handbookFile}
                  onLoadSuccess={onDocumentLoadSuccess}
                  loading={<Spin size="large" />}
                >
                  <Page
                    pageNumber={pageNumber}
                    scale={scale}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </Document>
              </div>
            ) : <Text type="secondary">No handbook available.</Text>}
          </div>
        </Content>
      </Layout>

      {/* Modals */}
      <Modal
        title="Upload New Handbook"
        open={uploadModalVisible}
        onCancel={() => setUploadModalVisible(false)}
        footer={null}
        width={500}
        centered
      >
        <Dragger
          name="handbook"
          multiple={false}
          accept=".pdf"
          beforeUpload={handleUpload}
          showUploadList={false}
          disabled={uploading}
        >
          <p className="ant-upload-drag-icon"><FilePdfOutlined style={{ fontSize: '48px', color: '#1677ff' }} /></p>
          <p className="ant-upload-text font-semibold">Click or drag PDF file</p>
        </Dragger>
      </Modal>

      <Modal
        title={
          <div className={`p-4 rounded-t-xl flex items-center gap-3 transition-all duration-200 ${isDarkMode ? 'bg-gray-900 border-b border-gray-800' : 'bg-slate-50 border-b border-slate-100'}`}>
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shadow-sm ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
              <HistoryOutlined className="text-blue-500" />
            </div>
            <span className={`text-base font-bold ${isDarkMode ? 'text-gray-100' : 'text-slate-800'}`}>Handbook Version History</span>
          </div>
        }
        open={historyModalVisible}
        onCancel={() => setHistoryModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setHistoryModalVisible(false)} className={`rounded-xl px-8 font-semibold shadow-sm h-10 ${isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-300 hover:text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>Close</Button>
        ]}
        width={1100}
        centered
        className={`xl-height-modal ${isDarkMode ? 'dark-modal' : ''}`}
        bodyStyle={{ padding: '0', height: 'calc(80vh - 140px)', backgroundColor: isDarkMode ? '#000' : '#fff' }}
        closeIcon={<span className={isDarkMode ? 'text-gray-400 hover:text-white transition-colors' : 'text-slate-400 hover:text-slate-600 transition-colors'}>×</span>}
      >
        <div className="p-6">
          <Table
            columns={historyColumns}
            dataSource={uploadHistory}
            rowKey="_id"
            pagination={{ 
              pageSize: 15,
              className: isDarkMode ? 'custom-pagination-dark' : ''
            }}
            size="middle"
            className="custom-theme-table"
            scroll={{ x: 1000 }}
          />
        </div>
      </Modal>

      <Modal
        title={
          <div className={`p-4 rounded-t-xl flex items-center gap-3 transition-all duration-200 ${isDarkMode ? 'bg-gray-900 border-b border-gray-800' : 'bg-slate-50 border-b border-slate-100'}`}>
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shadow-sm ${isDarkMode ? 'bg-indigo-900/30' : 'bg-indigo-100'}`}>
              <TeamOutlined className="text-indigo-500" />
            </div>
            <span className={`text-base font-bold ${isDarkMode ? 'text-gray-100' : 'text-slate-800'}`}>Acknowledgment Tracking</span>
          </div>
        }
        open={trackingModalVisible}
        onCancel={() => setTrackingModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setTrackingModalVisible(false)} className={`rounded-xl px-8 font-semibold shadow-sm h-10 ${isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-300 hover:text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>Close</Button>
        ]}
        width={1100}
        centered
        className={`xl-height-modal ${isDarkMode ? 'dark-modal' : ''}`}
        bodyStyle={{ padding: '0', height: 'calc(80vh - 140px)', backgroundColor: isDarkMode ? '#000' : '#fff' }}
        closeIcon={<span className={isDarkMode ? 'text-gray-400 hover:text-white transition-colors' : 'text-slate-400 hover:text-slate-600 transition-colors'}>×</span>}
      >
        <div className="p-8">
          <Row gutter={[20, 20]} className="mb-8 font-nunito">
            <Col xs={12} sm={6}>
              <Card size="small" className={`text-center shadow-sm border-0 rounded-2xl overflow-hidden relative group transition-all hover:shadow-md ${isDarkMode ? 'bg-slate-900/40 border-slate-800' : 'bg-blue-50/50 border-blue-100'}`}>
                <div className="absolute top-0 left-0 w-1 h-full bg-blue-500/80"></div>
                <Text type="secondary" className="block text-[10px] font-bold uppercase tracking-widest mb-1 opacity-70">Total Notified</Text>
                <Title level={2} className={`m-0 font-extrabold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{trackingData.stats?.total || 0}</Title>
                <div className={`text-[10px] mt-1 font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Active Employees</div>
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small" className={`text-center shadow-sm border-0 rounded-2xl overflow-hidden relative group transition-all hover:shadow-md ${isDarkMode ? 'bg-slate-900/40 border-slate-800' : 'bg-green-50/50 border-green-100'}`}>
                <div className="absolute top-0 left-0 w-1 h-full bg-green-500/80"></div>
                <Text type="secondary" className="block text-[10px] font-bold uppercase tracking-widest mb-1 opacity-70">Confirmed</Text>
                <Title level={2} className={`m-0 font-extrabold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>{trackingData.stats?.acknowledged || 0}</Title>
                <div className={`text-[10px] mt-1 font-medium ${isDarkMode ? 'text-green-500/80' : 'text-green-700/60'}`}>Accepted handbook</div>
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small" className={`text-center shadow-sm border-0 rounded-2xl overflow-hidden relative group transition-all hover:shadow-md ${isDarkMode ? 'bg-slate-900/40 border-slate-800' : 'bg-orange-50/50 border-orange-100'}`}>
                <div className="absolute top-0 left-0 w-1 h-full bg-orange-500/80"></div>
                <Text type="secondary" className="block text-[10px] font-bold uppercase tracking-widest mb-1 opacity-70">Pending</Text>
                <Title level={2} className={`m-0 font-extrabold ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>{trackingData.stats?.pending || 0}</Title>
                <div className={`text-[10px] mt-1 font-medium ${isDarkMode ? 'text-orange-500/80' : 'text-orange-700/60'}`}>Follow-up required</div>
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small" className={`text-center shadow-sm border-0 rounded-2xl overflow-hidden relative group transition-all hover:shadow-md ${isDarkMode ? 'bg-slate-900/40 border-slate-800' : 'bg-indigo-50/50 border-indigo-100'}`}>
                <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500/80"></div>
                <Text type="secondary" className="block text-[10px] font-bold uppercase tracking-widest mb-1 opacity-70">Compliance</Text>
                <Title level={2} className={`m-0 font-extrabold ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>{trackingData.stats?.percentage || 0}%</Title>
                <div className={`text-[10px] mt-1 font-medium ${isDarkMode ? 'text-indigo-500/80' : 'text-indigo-700/60'}`}>Completion rate</div>
              </Card>
            </Col>
          </Row>
          <div className={`rounded-2xl border overflow-hidden shadow-sm transition-colors duration-200 ${isDarkMode ? 'border-gray-800 bg-[#111]' : 'border-slate-100 bg-white'}`}>
            <Table
              columns={trackingColumns}
              dataSource={trackingData.list}
              rowKey="_id"
              pagination={{ 
                pageSize: 15,
                className: isDarkMode ? 'custom-pagination-dark' : ''
              }}
              size="middle"
              className="custom-theme-table"
              scroll={{ x: 800 }}
            />
          </div>
        </div>
      </Modal>

      <style jsx global>{`
        .xl-height-modal .ant-modal-content {
          height: 80vh !important;
          display: flex !important;
          flex-direction: column !important;
          border-radius: 20px !important;
          overflow: hidden !important;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
        }

        .dark-modal .ant-modal-content {
          background-color: #000 !important;
          border: 1px solid #1e293b !important;
        }

        .dark-modal .ant-modal-header {
          background-color: #0f172a !important;
          border-bottom: 1px solid #1e293b !important;
        }

        .dark-modal .ant-modal-footer {
          background-color: #0f172a !important;
          border-top: 1px solid #1e293b !important;
        }

        .xl-height-modal .ant-modal-header {
          padding: 0 !important;
          flex: 0 0 auto !important;
        }
        .xl-height-modal .ant-modal-body {
          flex: 1 1 auto !important;
          overflow-y: auto !important;
          scrollbar-width: thin;
          scrollbar-color: #94a3b8 transparent;
        }
        .xl-height-modal .ant-modal-footer {
          margin: 0 !important;
          padding: 16px 24px !important;
          flex: 0 0 auto !important;
        }

        .custom-theme-table .ant-table {
          background: transparent !important;
        }

        .custom-theme-table .ant-table-thead > tr > th {
          background: ${isDarkMode ? '#0f172a' : '#f8fafc'} !important;
          color: ${isDarkMode ? '#94a3b8' : '#64748b'} !important;
          border-bottom: 1px solid ${isDarkMode ? '#1e293b' : '#f1f5f9'} !important;
          font-weight: 800 !important;
          text-transform: uppercase !important;
          font-size: 10px !important;
          letter-spacing: 0.12em !important;
          padding: 16px !important;
        }

        .custom-theme-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid ${isDarkMode ? '#111' : '#f8fafc'} !important;
          padding: 14px 16px !important;
        }

        .custom-theme-table .ant-table-tbody > tr:hover > td {
          background: ${isDarkMode ? '#0f172a' : '#f8fafc'} !important;
        }

        .dark-modal .ant-pagination-item {
          background: #111 !important;
          border-color: #334155 !important;
        }

        .dark-modal .ant-pagination-item a {
          color: #94a3b8 !important;
        }

        .dark-modal .ant-pagination-item-active {
          border-color: #3b82f6 !important;
          background: #1e3a8a !important;
        }

        .dark-modal .ant-pagination-item-active a {
          color: #fff !important;
        }

        .dark-modal .ant-pagination-prev .ant-pagination-item-link,
        .dark-modal .ant-pagination-next .ant-pagination-item-link {
          background: #111 !important;
          border-color: #334155 !important;
          color: #94a3b8 !important;
        }

        .font-nunito {
          font-family: 'Nunito', sans-serif !important;
        }
      `}</style>
    </Layout>
  );
};

export default EmployeeHandbook;
